/**
 * Host Global - Admin Properties Management
 * Main page with tabs for different property statuses
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";

// react-big-calendar
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

// moment.js localizer for react-big-calendar
const localizer = momentLocalizer(moment);

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React examples
import DataTable from "examples/Tables/DataTable";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { adminAPI } from "utils/api";
import { getStatusLabel } from "utils/statusLabels";
import { PROPERTY_STATUS_OPTIONS } from "constants/propertyStatusOptions";

// Icons
import Icon from "@mui/material/Icon";

// Main Component
function AdminProperties() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [readyForInspectorFilter, setReadyForInspectorFilter] = useState("all");
  const [meetingRequestedFilter, setMeetingRequestedFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [propertyDetailsOpen, setPropertyDetailsOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedInspector, setSelectedInspector] = useState("");
  const [inspectorCalendar, setInspectorCalendar] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [calendarView, setCalendarView] = useState("month");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [dateConflict, setDateConflict] = useState(null);
  const [assignFormData, setAssignFormData] = useState({
    inspectorId: "",
    inspectionDate: "",
    inspectionLocation: "",
  });
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [pdfLightboxOpen, setPdfLightboxOpen] = useState(false);
  const [pdfLightboxUrl, setPdfLightboxUrl] = useState("");
  const [pdfLightboxTitle, setPdfLightboxTitle] = useState("");
  const [useGoogleViewer, setUseGoogleViewer] = useState(false);

  // Statistics state (computed from filtered list)
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inspection: 0,
    inspectionCompleted: 0,
    approved: 0,
    rejected: 0,
    readyForInspection: 0,
    notReadyForInspection: 0,
    byStatus: {},
  });

  // Helper to add Cloudinary download transformation
  const addDownloadTransformation = (url) => {
    if (!url || !url.includes("cloudinary.com")) return url;

    try {
      const urlObj = new URL(url);
      // Add attachment query parameter to force download
      urlObj.searchParams.set("attachment", "true");
      return urlObj.toString();
    } catch (error) {
      // If URL parsing fails, try string manipulation
      if (url.includes("?")) {
        return url + "&attachment=true";
      } else {
        return url + "?attachment=true";
      }
    }
  };

  // Open PDF in lightbox viewer
  const viewPdf = (url, title = "Document") => {
    if (!url) return;

    let fixedUrl = url;

    // For Cloudinary URLs, ensure they're properly formatted for embedding
    // Remove any attachment parameters that might interfere with viewing
    if (fixedUrl.includes("cloudinary.com")) {
      try {
        const urlObj = new URL(fixedUrl);
        urlObj.searchParams.delete("attachment");
        fixedUrl = urlObj.toString();
      } catch (e) {
        // If URL parsing fails, just use the URL as is
      }
    }

    setPdfLightboxUrl(fixedUrl);
    setPdfLightboxTitle(title);
    setUseGoogleViewer(false); // Reset to try object tag first
    setPdfLightboxOpen(true);
  };

  // Map status filter to API param (single workflow step value)
  const getStatusApiParam = (filter) => {
    if (!filter || filter === "all") return null;
    return filter;
  };

  // Fetch properties based on status and ready-for-inspection filters
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = {};
        const statusParam = getStatusApiParam(statusFilter);
        if (statusParam) params.status = statusParam;
        if (readyForInspectorFilter === "true") params.readyForInspector = "true";
        if (readyForInspectorFilter === "false") params.readyForInspector = "false";
        const [propertiesResponse, inspectorsResponse] = await Promise.all([
          adminAPI.getAllProperties(params),
          adminAPI.getAvailableInspectors(),
        ]);
        const propertiesList = propertiesResponse.properties || [];
        setProperties(propertiesList);
        setInspectors(inspectorsResponse.inspectors || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setMessage({
          type: "error",
          text: error.message || "Failed to load data. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [statusFilter, readyForInspectorFilter, refreshTrigger]);

  // Compute statistics from current filtered list (table data)
  useEffect(() => {
    const total = filteredProperties.length;
    const byStatus = {};
    PROPERTY_STATUS_OPTIONS.forEach(({ value }) => {
      byStatus[value] = filteredProperties.filter((p) => p.status === value).length;
    });
    const readyForInspection = filteredProperties.filter(
      (p) => p.readyForInspector === true
    ).length;
    const notReadyForInspection = total - readyForInspection;
    setStats({
      total,
      pending: filteredProperties.filter((p) =>
        ["Pending", "Request_Submission"].includes(p.status)
      ).length,
      inspection: filteredProperties.filter((p) =>
        ["Inspection", "First_Inspection", "Second_Inspection"].includes(p.status)
      ).length,
      inspectionCompleted: filteredProperties.filter((p) =>
        ["Inspection_Offer", "Inspection_Completed"].includes(p.status)
      ).length,
      approved: filteredProperties.filter((p) => p.status === "Approved" || p.status === "Active")
        .length,
      rejected: filteredProperties.filter((p) => p.status === "Rejected").length,
      readyForInspection,
      notReadyForInspection,
      byStatus,
    });
  }, [filteredProperties]);

  // Filter properties based on contract-response filter and search query
  useEffect(() => {
    let base = properties;
    if (meetingRequestedFilter !== "all") {
      base = properties.filter((p) => {
        const inContractPhase = p.status === "Inspection_Offer" || p.status === "Contracting";
        return inContractPhase && p.contractResponse === meetingRequestedFilter;
      });
    }
    if (!searchQuery.trim()) {
      setFilteredProperties(base);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = base.filter((property) => {
      const shortId = property.shortId?.toLowerCase() || "";
      const titleEn = property.title?.en?.toLowerCase() || property.title?.toLowerCase() || "";
      const titleAr = property.title?.ar?.toLowerCase() || "";
      const addressEn =
        property.address?.en?.toLowerCase() || property.address?.toLowerCase() || "";
      const addressAr = property.address?.ar?.toLowerCase() || "";
      const cityEn = property.city?.en?.toLowerCase() || property.city?.toLowerCase() || "";
      const cityAr = property.city?.ar?.toLowerCase() || "";
      const countryEn =
        property.country?.en?.toLowerCase() || property.country?.toLowerCase() || "";
      const countryAr = property.country?.ar?.toLowerCase() || "";

      return (
        shortId.includes(query) ||
        titleEn.includes(query) ||
        titleAr.includes(query) ||
        addressEn.includes(query) ||
        addressAr.includes(query) ||
        cityEn.includes(query) ||
        cityAr.includes(query) ||
        countryEn.includes(query) ||
        countryAr.includes(query)
      );
    });

    setFilteredProperties(filtered);
  }, [searchQuery, properties, meetingRequestedFilter]);

  // Fetch full property details when opening details dialog
  const fetchPropertyDetails = async (propertyId) => {
    try {
      const response = await adminAPI.getPropertyById(propertyId);
      return response.property;
    } catch (error) {
      console.error("Error fetching property details:", error);
      return null;
    }
  };

  // Handle card click - navigate to details page
  const handleCardClick = (property) => {
    navigate(`/admin/properties/${property._id || property.id}`);
  };

  // Helper function to get multilingual text
  const getMultilingualText = (text, defaultText = "") => {
    if (!text) return defaultText;
    if (typeof text === "string") return text;
    if (typeof text === "object" && text.en) return text.en;
    if (typeof text === "object" && text.ar) return text.ar;
    return defaultText;
  };

  // Fetch inspector calendar when inspector is selected
  useEffect(() => {
    const fetchCalendar = async () => {
      if (!selectedInspector) {
        setInspectorCalendar(null);
        return;
      }

      try {
        setLoadingCalendar(true);
        const response = await adminAPI.getInspectorCalendar(selectedInspector);
        setInspectorCalendar(response);
      } catch (error) {
        console.error("Error fetching inspector calendar:", error);
        setMessage({
          type: "error",
          text: error.message || "Failed to load inspector calendar.",
        });
      } finally {
        setLoadingCalendar(false);
      }
    };

    if (assignDialogOpen) {
      fetchCalendar();
    }
  }, [selectedInspector, assignDialogOpen]);

  // Check if selected date conflicts with inspector's schedule
  const checkDateConflict = (selectedDate, selectedEndDate) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections || !selectedDate) {
      return null;
    }

    const selectedDateTime = new Date(selectedDate);
    const selectedEndTime = selectedEndDate
      ? new Date(selectedEndDate)
      : new Date(selectedDateTime);

    const conflict = inspectorCalendar.inspections.find((inspection) => {
      // Skip the current property's inspection if reassigning
      if (
        selectedProperty &&
        (inspection.id === selectedProperty._id || inspection._id === selectedProperty._id)
      ) {
        return false;
      }

      const inspectionDate = new Date(inspection.inspectionDate);
      const inspectionEndTime = new Date(inspectionDate);
      inspectionEndTime.setHours(inspectionEndTime.getHours() + 1);

      // Check if there's an overlap
      return (
        (selectedDateTime >= inspectionDate && selectedDateTime < inspectionEndTime) ||
        (selectedEndTime > inspectionDate && selectedEndTime <= inspectionEndTime) ||
        (selectedDateTime <= inspectionDate && selectedEndTime >= inspectionEndTime)
      );
    });

    return conflict || null;
  };

  // Convert inspector inspections to calendar events
  const getCalendarEvents = () => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) return [];

    const events = inspectorCalendar.inspections
      .map((inspection) => {
        // Skip the current property's inspection if reassigning
        if (
          selectedProperty &&
          (inspection.id === selectedProperty._id || inspection._id === selectedProperty._id)
        ) {
          return null;
        }

        const startDate = new Date(inspection.inspectionDate);
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);

        const timeStr = startDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });

        const inspectionTitle = getMultilingualText(
          inspection.title,
          inspection.propertyTitle || `Inspection - ${timeStr}`
        );

        return {
          title: inspectionTitle,
          start: startDate,
          end: endDate,
          resource: {
            id: inspection.id,
            title: inspectionTitle,
            address: getMultilingualText(inspection.address, inspection.propertyAddress || "N/A"),
            city: getMultilingualText(inspection.city, inspection.propertyCity || "N/A"),
            location: inspection.inspectionLocation,
            status: inspection.status,
            isExisting: true,
          },
        };
      })
      .filter(Boolean);

    // Add selected slot as a temporary event for visual feedback
    if (selectedSlot) {
      events.push({
        title: "Selected Time",
        start: selectedSlot.start,
        end: selectedSlot.end,
        resource: {
          id: "selected",
          isSelected: true,
        },
      });
    }

    return events;
  };

  // Helper function to format date for datetime-local input
  const formatDateTimeLocal = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Handle assign/reassign inspector
  const handleOpenAssignDialog = (property) => {
    setSelectedProperty(property);
    // Pre-fill inspector if already assigned (for reassignment)
    const currentInspectorId = property.assignedInspector?._id || property.assignedInspector || "";
    setSelectedInspector(currentInspectorId);

    // Format inspection date for datetime-local input
    let formattedDate = "";
    if (property.inspectionDate) {
      formattedDate = formatDateTimeLocal(property.inspectionDate);
      const selectedDateTime = new Date(property.inspectionDate);
      selectedDateTime.setMinutes(0, 0, 0);
      const endDateTime = new Date(selectedDateTime);
      endDateTime.setHours(endDateTime.getHours() + 1);
      setSelectedSlot({
        start: selectedDateTime,
        end: endDateTime,
      });
    } else {
      setSelectedSlot(null);
    }

    setAssignFormData({
      inspectorId: currentInspectorId,
      inspectionDate: formattedDate,
      inspectionLocation:
        property.inspectionLocation ||
        getMultilingualText(property.address, property.address || ""),
    });
    setDateConflict(null);
    setCalendarDate(property.inspectionDate ? new Date(property.inspectionDate) : new Date());
    setAssignDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    if (!submitting) {
      setAssignDialogOpen(false);
      setSelectedProperty(null);
      setSelectedInspector("");
      setInspectorCalendar(null);
      setSelectedSlot(null);
      setDateConflict(null);
      setAssignFormData({
        inspectorId: "",
        inspectionDate: "",
        inspectionLocation: "",
      });
    }
  };

  // Handle inspector selection change
  const handleInspectorChange = (inspectorId) => {
    setSelectedInspector(inspectorId);
    setAssignFormData((prev) => ({ ...prev, inspectorId }));
    setSelectedSlot(null);
    setAssignFormData((prev) => ({ ...prev, inspectionDate: "" }));
    setDateConflict(null);
  };

  // Handle slot selection from calendar
  const handleSelectSlot = (slotInfo) => {
    if (!selectedInspector) {
      setMessage({
        type: "error",
        text: "Please select an inspector first.",
      });
      return;
    }

    const start = new Date(slotInfo.start);
    const end = new Date(slotInfo.end);

    // Round to nearest hour
    start.setMinutes(0, 0, 0);
    end.setMinutes(0, 0, 0);

    // If end is same as start, add 1 hour
    if (end.getTime() === start.getTime()) {
      end.setHours(end.getHours() + 1);
    }

    setSelectedSlot({ start, end });
    setAssignFormData((prev) => ({
      ...prev,
      inspectionDate: formatDateTimeLocal(start),
    }));

    // Check for conflicts
    const conflict = checkDateConflict(start.toISOString(), end.toISOString());
    setDateConflict(conflict);

    if (conflict) {
      setMessage({
        type: "error",
        text: `Inspector has another inspection at ${new Date(
          conflict.inspectionDate
        ).toLocaleString()}. Please choose a different time.`,
      });
    } else {
      setMessage({ type: "", text: "" });
    }
  };

  // Handle event click (view existing inspection details)
  const handleSelectEvent = (event) => {
    if (event.resource?.isExisting) {
      // Show inspection details
      console.log("Inspection details:", event.resource);
    }
  };

  // Navigate calendar
  const navigateCalendar = (action) => {
    const newDate = new Date(calendarDate);
    if (calendarView === "month") {
      newDate.setMonth(
        action === "PREV" ? calendarDate.getMonth() - 1 : calendarDate.getMonth() + 1
      );
    } else if (calendarView === "week") {
      newDate.setDate(action === "PREV" ? calendarDate.getDate() - 7 : calendarDate.getDate() + 7);
    } else if (calendarView === "day") {
      newDate.setDate(action === "PREV" ? calendarDate.getDate() - 1 : calendarDate.getDate() + 1);
    }
    setCalendarDate(newDate);
  };

  // Handle view change
  const handleViewChange = (view) => {
    setCalendarView(view);
  };

  // Event style getter
  const eventStyleGetter = (event) => {
    const isExisting = event.resource?.isExisting;
    const isSelected = event.resource?.isSelected;

    let backgroundColor = "#3174ad"; // Default blue
    if (isSelected) {
      backgroundColor = "#4CAF50"; // Green for selected
    } else if (isExisting) {
      backgroundColor = "#f44336"; // Red for existing
    }

    return {
      style: {
        backgroundColor,
        borderRadius: "4px",
        opacity: 0.9,
        color: "white",
        border: "none",
        display: "flex",
        alignItems: "center",
        padding: "6px 10px",
        fontWeight: 500,
        fontSize: "0.875rem",
        minHeight: "28px",
        whiteSpace: "normal",
        wordWrap: "break-word",
        lineHeight: "1.4",
      },
    };
  };

  // Slot prop getter (for reserved slots)
  const slotPropGetter = (date) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) {
      return {};
    }

    const isReserved = inspectorCalendar.inspections.some((inspection) => {
      // Skip current property if reassigning
      if (
        selectedProperty &&
        (inspection.id === selectedProperty._id || inspection._id === selectedProperty._id)
      ) {
        return false;
      }

      const inspectionDate = new Date(inspection.inspectionDate);
      const inspectionEndTime = new Date(inspectionDate);
      inspectionEndTime.setHours(inspectionEndTime.getHours() + 1);

      return date >= inspectionDate && date < inspectionEndTime;
    });

    if (isReserved) {
      return {
        className: "rbc-slot-reserved",
      };
    }

    return {};
  };

  const handleAssignInspector = async () => {
    if (!selectedProperty) return;

    if (
      !selectedInspector ||
      !assignFormData.inspectionDate ||
      !assignFormData.inspectionLocation.trim()
    ) {
      setMessage({
        type: "error",
        text: "Please fill in all required fields: Inspector, Date, and Location.",
      });
      return;
    }

    // Check for conflicts using selected slot times if available
    const inspectionStart = new Date(assignFormData.inspectionDate);
    const inspectionEnd = selectedSlot?.end
      ? new Date(selectedSlot.end)
      : new Date(inspectionStart);

    // If no end time from selected slot, use 1 hour default
    if (!selectedSlot?.end || inspectionEnd.getTime() === inspectionStart.getTime()) {
      inspectionEnd.setHours(inspectionEnd.getHours() + 1);
    }

    const conflict = checkDateConflict(inspectionStart.toISOString(), inspectionEnd.toISOString());
    if (conflict) {
      setMessage({
        type: "error",
        text: `Inspector is already assigned to another inspection at ${new Date(
          conflict.inspectionDate
        ).toLocaleString()}. Please choose a different time.`,
      });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      // Convert datetime-local format to ISO string for backend
      const localDate = new Date(assignFormData.inspectionDate);
      if (isNaN(localDate.getTime())) {
        setMessage({
          type: "error",
          text: "Invalid date format. Please select a valid date and time.",
        });
        setSubmitting(false);
        return;
      }
      const isoDateString = localDate.toISOString();

      const response = await adminAPI.assignInspector(selectedProperty._id || selectedProperty.id, {
        inspectorId: selectedInspector,
        inspectionDate: isoDateString,
        inspectionLocation: assignFormData.inspectionLocation.trim(),
      });

      if (response.message) {
        const isReassign = selectedProperty.assignedInspector;
        setMessage({
          type: "success",
          text: isReassign
            ? `Inspector reassigned successfully! ${response.message}`
            : response.message,
        });

        // Refresh properties list
        const propertiesResponse = await adminAPI.getAllProperties(
          statusFilter !== "all" ? { status: statusFilter } : {}
        );
        const propertiesList = propertiesResponse.properties || [];
        setProperties(propertiesList);

        // Refresh statistics
        const allPropertiesResponse = await adminAPI.getAllProperties({});
        const allProperties = allPropertiesResponse.properties || [];
        setStats({
          total: allProperties.length,
          pending: allProperties.filter((p) => ["Pending", "Request_Submission"].includes(p.status))
            .length,
          inspection: allProperties.filter((p) =>
            ["Inspection", "First_Inspection", "Second_Inspection"].includes(p.status)
          ).length,
          inspectionCompleted: allProperties.filter((p) =>
            ["Inspection_Offer", "Inspection_Completed"].includes(p.status)
          ).length,
          approved: allProperties.filter((p) => p.status === "Approved" || p.status === "Active")
            .length,
          rejected: allProperties.filter((p) => p.status === "Rejected").length,
        });

        setAssignDialogOpen(false);
        setSelectedProperty(null);
        setSelectedInspector("");
        setInspectorCalendar(null);
        setSelectedSlot(null);
        setDateConflict(null);
        setAssignFormData({
          inspectorId: "",
          inspectionDate: "",
          inspectionLocation: "",
        });
      }
    } catch (error) {
      console.error("Error assigning inspector:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to assign inspector. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reject property
  const handleOpenRejectDialog = (property) => {
    setSelectedProperty(property);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const handleCloseRejectDialog = () => {
    if (!submitting) {
      setRejectDialogOpen(false);
      setSelectedProperty(null);
      setRejectReason("");
    }
  };

  const handleRejectProperty = async () => {
    if (!selectedProperty || !rejectReason.trim()) return;

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const response = await adminAPI.rejectProperty(
        selectedProperty._id || selectedProperty.id,
        rejectReason.trim()
      );

      if (response.message) {
        setMessage({
          type: "success",
          text: response.message,
        });

        // Refresh properties list
        const propertiesResponse = await adminAPI.getAllProperties(
          statusFilter !== "all" ? { status: statusFilter } : {}
        );
        const propertiesList = propertiesResponse.properties || [];
        setProperties(propertiesList);

        // Refresh statistics
        const allPropertiesResponse = await adminAPI.getAllProperties({});
        const allProperties = allPropertiesResponse.properties || [];
        setStats({
          total: allProperties.length,
          pending: allProperties.filter((p) => ["Pending", "Request_Submission"].includes(p.status))
            .length,
          inspection: allProperties.filter((p) =>
            ["Inspection", "First_Inspection", "Second_Inspection"].includes(p.status)
          ).length,
          inspectionCompleted: allProperties.filter((p) =>
            ["Inspection_Offer", "Inspection_Completed"].includes(p.status)
          ).length,
          approved: allProperties.filter((p) => p.status === "Approved" || p.status === "Active")
            .length,
          rejected: allProperties.filter((p) => p.status === "Rejected").length,
        });

        setRejectDialogOpen(false);
        setSelectedProperty(null);
        setRejectReason("");
      }
    } catch (error) {
      console.error("Error rejecting property:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to reject property. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProperty = async () => {
    if (!propertyToDelete) return;
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });
      await adminAPI.deleteProperty(propertyToDelete._id || propertyToDelete.id);
      setMessage({ type: "success", text: "Property deleted successfully." });
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
      setRefreshTrigger((t) => t + 1);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "Failed to delete property. You may not have permission.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="medium">
            Properties Management
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Manage all properties, assign inspectors, and review submissions
          </MDTypography>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type === "success" ? "success" : "error"}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3} sx={{ "& .MuiGrid-item": { display: "flex" } }}>
          <Grid item xs={12} sm={6} md={4}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="dark"
                icon="home"
                title="Total"
                count={stats.total}
                percentage={{
                  color: "success",
                  amount: stats.total > 0 ? "100%" : "0%",
                  label: "of filtered",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "180px" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="warning"
                icon="schedule"
                title="Pending"
                count={stats.pending}
                percentage={{
                  color: "warning",
                  amount:
                    stats.total > 0 ? `${Math.round((stats.pending / stats.total) * 100)}%` : "0%",
                  label: "Awaiting review",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "180px" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="info"
                icon="search"
                title="Inspection"
                count={stats.inspection}
                percentage={{
                  color: "info",
                  amount:
                    stats.total > 0
                      ? `${Math.round((stats.inspection / stats.total) * 100)}%`
                      : "0%",
                  label: "Under inspection",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "180px" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="primary"
                icon="check_circle"
                title="Completed"
                count={stats.inspectionCompleted}
                percentage={{
                  color: "primary",
                  amount:
                    stats.total > 0
                      ? `${Math.round((stats.inspectionCompleted / stats.total) * 100)}%`
                      : "0%",
                  label: "Inspection done",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "180px" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="success"
                icon="verified"
                title="Approved"
                count={stats.approved}
                percentage={{
                  color: "success",
                  amount:
                    stats.total > 0 ? `${Math.round((stats.approved / stats.total) * 100)}%` : "0%",
                  label: "Active listings",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "180px" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="error"
                icon="cancel"
                title="Rejected"
                count={stats.rejected}
                percentage={{
                  color: "error",
                  amount:
                    stats.total > 0 ? `${Math.round((stats.rejected / stats.total) * 100)}%` : "0%",
                  label: "Not approved",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "180px" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="success"
                icon="visibility"
                title="Ready for Inspection"
                count={stats.readyForInspection}
                percentage={{
                  color: "success",
                  amount:
                    stats.total > 0
                      ? `${Math.round((stats.readyForInspection / stats.total) * 100)}%`
                      : "0%",
                  label: "Yes",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "180px" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="secondary"
                icon="visibility_off"
                title="Not Ready for Inspection"
                count={stats.notReadyForInspection}
                percentage={{
                  color: "secondary",
                  amount:
                    stats.total > 0
                      ? `${Math.round((stats.notReadyForInspection / stats.total) * 100)}%`
                      : "0%",
                  label: "No",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "180px" }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* Per workflow step counts (compact) */}
        <Card sx={{ mb: 2 }}>
          <MDBox p={2}>
            <MDTypography
              variant="button"
              fontWeight="medium"
              color="text"
              sx={{ mb: 1.5, display: "block" }}
            >
              By workflow step
            </MDTypography>
            <MDBox display="flex" flexWrap="wrap" gap={1}>
              {PROPERTY_STATUS_OPTIONS.map(({ value, label }) => {
                const count = stats.byStatus?.[value] ?? 0;
                if (count === 0) return null;
                const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                return (
                  <Chip
                    key={value}
                    label={`${label}: ${count} (${pct}%)`}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                );
              })}
              {Object.keys(stats.byStatus || {}).length === 0 ||
              Object.values(stats.byStatus || {}).every((c) => c === 0) ? (
                <MDTypography variant="caption" color="text.secondary">
                  No properties in current filter
                </MDTypography>
              ) : null}
            </MDBox>
          </MDBox>
        </Card>

        {/* Search Bar */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={2}>
            <TextField
              fullWidth
              placeholder="Search by Property ID, Title, Address, City, or Country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Icon sx={{ mr: 1, color: "text.secondary" }}>search</Icon>,
              }}
            />
          </MDBox>
        </Card>

        {/* Status and Ready for Inspection Filters */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={2}>
            <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <MDTypography variant="body2" fontWeight="medium" color="text" sx={{ mr: 1 }}>
                Filters:
              </MDTypography>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Status / Workflow step</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status / Workflow step"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {PROPERTY_STATUS_OPTIONS.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Ready for Inspection</InputLabel>
                <Select
                  value={readyForInspectorFilter}
                  label="Ready for Inspection"
                  onChange={(e) => setReadyForInspectorFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Contract response</InputLabel>
                <Select
                  value={meetingRequestedFilter}
                  label="Contract response"
                  onChange={(e) => setMeetingRequestedFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="meeting_requested">Meeting requested</MenuItem>
                  <MenuItem value="rejected">Declined</MenuItem>
                </Select>
              </FormControl>
              <Chip
                label="Clear filters"
                onClick={() => {
                  setStatusFilter("all");
                  setReadyForInspectorFilter("all");
                  setMeetingRequestedFilter("all");
                }}
                color="default"
                variant="outlined"
                sx={{ cursor: "pointer" }}
              />
            </MDBox>
          </MDBox>
        </Card>

        {/* Properties Table */}
        <Card>
          <MDBox p={3}>
            {loading ? (
              <MDBox p={3} textAlign="center">
                <MDTypography variant="body2" color="text">
                  Loading properties...
                </MDTypography>
              </MDBox>
            ) : filteredProperties.length === 0 ? (
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" mb={1}>
                  No Properties Found
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  {searchQuery.trim()
                    ? `No properties match your search query "${searchQuery}".`
                    : statusFilter === "all"
                    ? "No properties in the system yet."
                    : `No ${statusFilter
                        .replace(/_/g, " ")
                        .toLowerCase()} properties at the moment.`}
                </MDTypography>
              </MDBox>
            ) : (
              <DataTable
                table={{
                  columns: [
                    {
                      Header: "ID",
                      accessor: "shortId",
                      width: "8%",
                      Cell: ({ value }) => (
                        <MDTypography variant="caption" fontWeight="medium" color="text">
                          {value || "N/A"}
                        </MDTypography>
                      ),
                    },
                    {
                      Header: "Property",
                      accessor: "propertyInfo",
                      width: "25%",
                      Cell: ({ row }) => {
                        const property = row.original;
                        return (
                          <MDBox display="flex" alignItems="center" gap={1}>
                            {property.images && property.images.length > 0 ? (
                              <Avatar
                                src={property.images[0]}
                                alt={property.title?.en || "Property"}
                                sx={{ width: 48, height: 48, borderRadius: 1 }}
                              />
                            ) : (
                              <Avatar
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 1,
                                  bgcolor: "grey.300",
                                }}
                              >
                                <Icon>home</Icon>
                              </Avatar>
                            )}
                            <MDBox>
                              <MDTypography variant="button" fontWeight="medium" color="text">
                                {property.title?.en || property.title || "Untitled Property"}
                              </MDTypography>
                              <MDTypography variant="caption" color="text.secondary">
                                {property.propertyType} • {property.rooms} rooms •{" "}
                                {property.bathrooms} baths
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        );
                      },
                    },
                    {
                      Header: "Location",
                      accessor: "location",
                      width: "20%",
                      Cell: ({ row }) => {
                        const property = row.original;
                        return (
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="medium" color="text">
                              {property.city?.en || property.city || "N/A"},{" "}
                              {property.country?.en || property.country || "N/A"}
                            </MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              {property.address?.en || property.address || "N/A"}
                            </MDTypography>
                          </MDBox>
                        );
                      },
                    },
                    {
                      Header: "Owner",
                      accessor: "owner",
                      width: "12%",
                      Cell: ({ value }) => (
                        <MDTypography variant="caption" color="text">
                          {value?.fullName || "N/A"}
                        </MDTypography>
                      ),
                    },
                    {
                      Header: "Status",
                      accessor: "status",
                      width: "12%",
                      Cell: ({ row }) => {
                        const property = row.original;
                        const status = property.status;
                        const getStatusColor = (s) => {
                          if (["Approved", "Active"].includes(s)) return "success";
                          if (["Pending", "Request_Submission"].includes(s)) return "warning";
                          if (
                            [
                              "Inspection",
                              "First_Inspection",
                              "Inspection_Offer",
                              "Inspection_Completed",
                              "Second_Inspection",
                              "Preparation",
                              "Contracting",
                              "Legalization",
                              "Photo_Shooting",
                            ].includes(s)
                          )
                            return "info";
                          if (s === "Rejected") return "error";
                          return "secondary";
                        };
                        return (
                          <Chip
                            label={
                              getStatusLabel(status, "admin", {
                                hasInspector: !!property.assignedInspector,
                              }) || "N/A"
                            }
                            color={getStatusColor(status)}
                            size="small"
                            sx={{ fontWeight: "medium" }}
                          />
                        );
                      },
                    },
                    {
                      Header: "Documents",
                      accessor: "documents",
                      width: "8%",
                      Cell: ({ row }) => {
                        const property = row.original;
                        const docCount =
                          (property.officialDocuments?.length || 0) +
                          (property.legalPapers?.length || 0);
                        return (
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <Icon fontSize="small" color="action">
                              description
                            </Icon>
                            <MDTypography variant="caption" color="text">
                              {docCount}
                            </MDTypography>
                          </MDBox>
                        );
                      },
                    },
                    {
                      Header: "Actions",
                      accessor: "actions",
                      width: "20%",
                      align: "center",
                      Cell: ({ row }) => {
                        const property = row.original;
                        return (
                          <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleCardClick(property)}
                              >
                                <Icon fontSize="small">visibility</Icon>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Property">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setPropertyToDelete(property);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Icon fontSize="small">delete</Icon>
                              </IconButton>
                            </Tooltip>
                          </MDBox>
                        );
                      },
                    },
                  ],
                  rows: filteredProperties,
                }}
                isSorted={true}
                entriesPerPage={false}
                showTotalEntries={true}
                canSearch={false}
                noEndBorder
              />
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Property Details Dialog */}
      <Dialog
        open={propertyDetailsOpen}
        onClose={() => setPropertyDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold">
              Property Details
            </MDTypography>
            <IconButton onClick={() => setPropertyDetailsOpen(false)}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProperty && (
            <MDBox>
              {/* Property Information */}
              <MDBox mb={4}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                >
                  <Icon sx={{ mr: 1, color: "info.main" }}>home</Icon>
                  Property Information
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Title (English)
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.title?.en || selectedProperty.title || "N/A"}
                    </MDTypography>
                  </Grid>
                  {selectedProperty.title?.ar && (
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        Title (Arabic)
                      </MDTypography>
                      <MDTypography
                        variant="body1"
                        mb={1}
                        sx={{ direction: "rtl", textAlign: "right" }}
                      >
                        {selectedProperty.title.ar}
                      </MDTypography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Property Type
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.propertyType
                        ? selectedProperty.propertyType.charAt(0).toUpperCase() +
                          selectedProperty.propertyType.slice(1)
                        : "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Description (English)
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.description?.en ||
                        selectedProperty.description ||
                        "No description provided"}
                    </MDTypography>
                  </Grid>
                  {selectedProperty.description?.ar && (
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        Description (Arabic)
                      </MDTypography>
                      <MDTypography
                        variant="body1"
                        mb={1}
                        sx={{ direction: "rtl", textAlign: "right" }}
                      >
                        {selectedProperty.description.ar}
                      </MDTypography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Status
                    </MDTypography>
                    <MDBox mt={0.5}>
                      <Chip
                        label={selectedProperty.status.replace(/_/g, " ")}
                        color={
                          selectedProperty.status === "Approved"
                            ? "success"
                            : selectedProperty.status === "Pending"
                            ? "warning"
                            : selectedProperty.status === "Rejected"
                            ? "error"
                            : "info"
                        }
                        size="small"
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Rooms
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.rooms || "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Bathrooms
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.bathrooms || "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Size (sq ft)
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.size ? `${selectedProperty.size} sq ft` : "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Price per Night
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.price
                        ? `${selectedProperty.price.toLocaleString()} EGP`
                        : "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Address (English)
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.address?.en || selectedProperty.address || "N/A"}
                    </MDTypography>
                  </Grid>
                  {selectedProperty.address?.ar && (
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        Address (Arabic)
                      </MDTypography>
                      <MDTypography
                        variant="body1"
                        mb={1}
                        sx={{ direction: "rtl", textAlign: "right" }}
                      >
                        {selectedProperty.address.ar}
                      </MDTypography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      City (English)
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.city?.en || selectedProperty.city || "N/A"}
                    </MDTypography>
                  </Grid>
                  {selectedProperty.city?.ar && (
                    <Grid item xs={12} md={4}>
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        City (Arabic)
                      </MDTypography>
                      <MDTypography
                        variant="body1"
                        mb={1}
                        sx={{ direction: "rtl", textAlign: "right" }}
                      >
                        {selectedProperty.city.ar}
                      </MDTypography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Country (English)
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.country?.en || selectedProperty.country || "N/A"}
                    </MDTypography>
                  </Grid>
                  {selectedProperty.country?.ar && (
                    <Grid item xs={12} md={4}>
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        Country (Arabic)
                      </MDTypography>
                      <MDTypography
                        variant="body1"
                        mb={1}
                        sx={{ direction: "rtl", textAlign: "right" }}
                      >
                        {selectedProperty.country.ar}
                      </MDTypography>
                    </Grid>
                  )}
                  {selectedProperty.googleMapLink && (
                    <Grid item xs={12}>
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        Google Map Link
                      </MDTypography>
                      <MDBox mt={0.5}>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          href={selectedProperty.googleMapLink}
                          target="_blank"
                          startIcon={<Icon>map</Icon>}
                        >
                          View on Google Maps
                        </MDButton>
                      </MDBox>
                    </Grid>
                  )}
                  {selectedProperty.amenities && selectedProperty.amenities.length > 0 && (
                    <Grid item xs={12}>
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontWeight="medium"
                        display="block"
                        mb={1}
                      >
                        Amenities
                      </MDTypography>
                      <MDBox display="flex" flexWrap="wrap" gap={1}>
                        {selectedProperty.amenities.map((amenity, index) => {
                          const amenityLabel =
                            typeof amenity === "object" && amenity.en
                              ? amenity.en
                              : typeof amenity === "string"
                              ? amenity
                              : "";
                          return <Chip key={index} label={amenityLabel} size="small" />;
                        })}
                      </MDBox>
                    </Grid>
                  )}
                </Grid>
              </MDBox>

              <Divider sx={{ my: 3 }} />

              {/* Property Images */}
              {selectedProperty.images && selectedProperty.images.length > 0 && (
                <MDBox mb={4}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    display="flex"
                    alignItems="center"
                  >
                    <Icon sx={{ mr: 1, color: "info.main" }}>image</Icon>
                    Property Images ({selectedProperty.images.length})
                  </MDTypography>
                  <Grid container spacing={2}>
                    {selectedProperty.images.map((image, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper
                          elevation={2}
                          sx={{
                            position: "relative",
                            padding: 1,
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            component="img"
                            src={image}
                            alt={`Property image ${index + 1}`}
                            sx={{
                              width: "100%",
                              height: 200,
                              objectFit: "cover",
                              borderRadius: 1,
                              cursor: "pointer",
                            }}
                            onClick={() => window.open(image, "_blank")}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </MDBox>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Property Videos */}
              {selectedProperty.videos && selectedProperty.videos.length > 0 && (
                <MDBox mb={4}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    display="flex"
                    alignItems="center"
                  >
                    <Icon sx={{ mr: 1, color: "info.main" }}>videocam</Icon>
                    Property Videos ({selectedProperty.videos.length})
                  </MDTypography>
                  <Grid container spacing={2}>
                    {selectedProperty.videos.map((video, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper elevation={2} sx={{ p: 1, borderRadius: 2 }}>
                          <Box
                            component="video"
                            src={video}
                            controls
                            sx={{
                              width: "100%",
                              maxHeight: 200,
                              borderRadius: 1,
                            }}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </MDBox>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Property Official Documents */}
              {selectedProperty.officialDocuments &&
                selectedProperty.officialDocuments.length > 0 && (
                  <MDBox mb={4}>
                    <MDTypography
                      variant="h6"
                      fontWeight="bold"
                      mb={2}
                      display="flex"
                      alignItems="center"
                    >
                      <Icon sx={{ mr: 1, color: "info.main" }}>description</Icon>
                      Property Official Documents ({selectedProperty.officialDocuments.length})
                    </MDTypography>
                    <Grid container spacing={2}>
                      {selectedProperty.officialDocuments.map((doc, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <MDBox
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <MDBox display="flex" alignItems="center">
                                <Icon sx={{ mr: 1, color: "error.main" }}>description</Icon>
                                <MDTypography variant="body2">Document {index + 1}</MDTypography>
                              </MDBox>
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<Icon>visibility</Icon>}
                                onClick={() => viewPdf(doc, `Official Document ${index + 1}`)}
                              >
                                View
                              </MDButton>
                            </MDBox>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                )}

              <Divider sx={{ my: 3 }} />

              {/* Owner Information */}
              <MDBox mb={4}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                >
                  <Icon sx={{ mr: 1, color: "info.main" }}>person</Icon>
                  Property Owner Information
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Full Name
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.owner?.fullName || "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Email
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.owner?.email || "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Phone
                    </MDTypography>
                    <MDTypography variant="body1" mb={1}>
                      {selectedProperty.owner?.phone || "N/A"}
                    </MDTypography>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Owner Documents */}
              {selectedProperty.owner && selectedProperty.ownerDocuments && (
                <MDBox>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    display="flex"
                    alignItems="center"
                  >
                    <Icon sx={{ mr: 1, color: "info.main" }}>folder</Icon>
                    Owner Documents
                  </MDTypography>

                  {/* Official Documents */}
                  {selectedProperty.ownerDocuments.officialDocuments &&
                    selectedProperty.ownerDocuments.officialDocuments.length > 0 && (
                      <MDBox mb={3}>
                        <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
                          Official Documents (
                          {selectedProperty.ownerDocuments.officialDocuments.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                          {selectedProperty.ownerDocuments.officialDocuments.map((doc, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <MDBox display="flex" alignItems="center" flex={1}>
                                    <Icon sx={{ mr: 1, color: "error.main" }}>description</Icon>
                                    <MDBox>
                                      <MDTypography variant="body2" fontWeight="medium">
                                        {doc.name || `Document ${index + 1}`}
                                      </MDTypography>
                                      {doc.uploadedAt && (
                                        <MDTypography variant="caption" color="text">
                                          {new Date(doc.uploadedAt).toLocaleDateString()}
                                        </MDTypography>
                                      )}
                                    </MDBox>
                                  </MDBox>
                                  <MDButton
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    href={doc.url}
                                    target="_blank"
                                    startIcon={<Icon>open_in_new</Icon>}
                                  >
                                    View
                                  </MDButton>
                                </MDBox>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </MDBox>
                    )}

                  {/* Document Bucket */}
                  {selectedProperty.ownerDocuments.documentBucket &&
                    selectedProperty.ownerDocuments.documentBucket.length > 0 && (
                      <MDBox>
                        <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
                          Document Bucket ({selectedProperty.ownerDocuments.documentBucket.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                          {selectedProperty.ownerDocuments.documentBucket.map((doc, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <MDBox display="flex" alignItems="center" flex={1}>
                                    <Icon sx={{ mr: 1, color: "warning.main" }}>folder</Icon>
                                    <MDBox>
                                      <MDTypography variant="body2" fontWeight="medium">
                                        {doc.name || `Document ${index + 1}`}
                                      </MDTypography>
                                      <MDTypography variant="caption" color="text">
                                        Uploaded by: {doc.uploadedBy || "N/A"}
                                      </MDTypography>
                                      {doc.uploadedAt && (
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                          display="block"
                                        >
                                          {new Date(doc.uploadedAt).toLocaleDateString()}
                                        </MDTypography>
                                      )}
                                      {doc.notes && (
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                          display="block"
                                          mt={0.5}
                                        >
                                          {doc.notes}
                                        </MDTypography>
                                      )}
                                    </MDBox>
                                  </MDBox>
                                  <MDButton
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    href={doc.url}
                                    target="_blank"
                                    startIcon={<Icon>open_in_new</Icon>}
                                  >
                                    View
                                  </MDButton>
                                </MDBox>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </MDBox>
                    )}

                  {(!selectedProperty.ownerDocuments.officialDocuments ||
                    selectedProperty.ownerDocuments.officialDocuments.length === 0) &&
                    (!selectedProperty.ownerDocuments.documentBucket ||
                      selectedProperty.ownerDocuments.documentBucket.length === 0) && (
                      <MDTypography variant="body2" color="text">
                        No owner documents available.
                      </MDTypography>
                    )}
                </MDBox>
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setPropertyDetailsOpen(false)}>Close</MDButton>
          {selectedProperty && selectedProperty.status === "Pending" && (
            <>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  setPropertyDetailsOpen(false);
                  handleOpenAssignDialog(selectedProperty);
                }}
                startIcon={<Icon>assignment</Icon>}
              >
                Assign Inspector
              </MDButton>
              <MDButton
                variant="outlined"
                color="error"
                onClick={() => {
                  setPropertyDetailsOpen(false);
                  handleOpenRejectDialog(selectedProperty);
                }}
                startIcon={<Icon>cancel</Icon>}
              >
                Reject
              </MDButton>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Assign/Reassign Inspector Dialog with Calendar */}
      <Dialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold">
              {selectedProperty?.assignedInspector ? "Reassign Inspector" : "Assign Inspector"}
            </MDTypography>
            <IconButton onClick={handleCloseAssignDialog} disabled={submitting}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Left Column - Form */}
            <Grid item xs={12} lg={4}>
              <MDBox>
                {selectedProperty && (
                  <MDBox p={2} borderRadius="lg" bgColor="grey.100" mb={3}>
                    <MDTypography variant="subtitle2" fontWeight="medium">
                      {getMultilingualText(selectedProperty.title, "N/A")}
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      {getMultilingualText(selectedProperty.address, "N/A")},{" "}
                      {getMultilingualText(selectedProperty.city, "N/A")}
                    </MDTypography>
                    {selectedProperty.assignedInspector && (
                      <MDBox mt={1}>
                        <MDTypography variant="caption" color="text.secondary">
                          Current Inspector:{" "}
                          {selectedProperty.assignedInspector?.fullName ||
                            selectedProperty.assignedInspector?.email ||
                            "N/A"}
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                )}

                {/* Ready for inspection, preferred time and property owner contact */}
                {selectedProperty && (
                  <MDBox
                    p={2}
                    borderRadius="lg"
                    bgColor="info.lighter"
                    mb={3}
                    sx={{ border: "1px solid", borderColor: "info.light" }}
                  >
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      color="info.main"
                      textTransform="uppercase"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Ready for inspection
                    </MDTypography>
                    <MDTypography variant="body2" color="text" sx={{ mb: 1.5 }}>
                      {selectedProperty.readyForInspector === true
                        ? "Yes"
                        : selectedProperty.readyForInspector === false
                        ? "No"
                        : "—"}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      color="info.main"
                      textTransform="uppercase"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Preferred inspection date / time (from owner)
                    </MDTypography>
                    <MDTypography variant="body2" color="text" sx={{ mb: 1.5 }}>
                      {selectedProperty.preferredInspectionTime || "—"}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      color="info.main"
                      textTransform="uppercase"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Property owner
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      Name: {selectedProperty.owner?.fullName || "—"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      Phone: {selectedProperty.owner?.phone || "—"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text" sx={{ mb: 1 }}>
                      Email: {selectedProperty.owner?.email || "—"}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" fontStyle="italic">
                      If the preferred time is not available, contact the owner to reschedule.
                    </MDTypography>
                  </MDBox>
                )}

                {/* Inspector Selection */}
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Inspector *</InputLabel>
                  <Select
                    value={selectedInspector}
                    onChange={(e) => handleInspectorChange(e.target.value)}
                    label="Inspector *"
                    disabled={submitting}
                  >
                    {inspectors.map((inspector) => (
                      <MenuItem
                        key={inspector._id || inspector.id}
                        value={inspector._id || inspector.id}
                      >
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Icon sx={{ fontSize: "1.25rem", color: "text.secondary" }}>person</Icon>
                          <MDBox>
                            <MDTypography variant="body2" fontWeight="medium">
                              {inspector.fullName}
                            </MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              {inspector.email}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Selected Inspector Info */}
                {selectedInspector &&
                  inspectors.find(
                    (insp) => String(insp._id || insp.id) === String(selectedInspector)
                  ) && (
                    <MDBox
                      mb={3}
                      p={2}
                      sx={{
                        bgcolor: "info.lighter",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <MDBox display="flex" alignItems="center" mb={1}>
                        <Icon sx={{ mr: 1, color: "info.main" }}>check_circle</Icon>
                        <MDTypography
                          variant="caption"
                          color="info.main"
                          fontWeight="bold"
                          textTransform="uppercase"
                        >
                          Selected Inspector
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" mt={1}>
                        <Icon sx={{ mr: 1, color: "text.secondary" }}>person</Icon>
                        <MDBox>
                          <MDTypography variant="body2" fontWeight="medium" color="dark">
                            {
                              inspectors.find(
                                (insp) => String(insp._id || insp.id) === String(selectedInspector)
                              )?.fullName
                            }
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            {
                              inspectors.find(
                                (insp) => String(insp._id || insp.id) === String(selectedInspector)
                              )?.email
                            }
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  )}

                {/* Inspection Date */}
                <MDBox mb={3}>
                  <MDTypography
                    variant="caption"
                    fontWeight="medium"
                    color="text.secondary"
                    sx={{
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    Inspection Date & Time *
                  </MDTypography>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    name="inspectionDate"
                    value={assignFormData.inspectionDate}
                    onChange={(e) => {
                      const dateValue = e.target.value;
                      setAssignFormData({ ...assignFormData, inspectionDate: dateValue });
                      // Update selected slot when date is manually changed
                      if (dateValue) {
                        const selectedDateTime = new Date(dateValue);
                        selectedDateTime.setMinutes(0, 0, 0);
                        const endDateTime = new Date(selectedDateTime);
                        endDateTime.setHours(endDateTime.getHours() + 1);
                        setSelectedSlot({
                          start: selectedDateTime,
                          end: endDateTime,
                        });
                        // Check for conflicts
                        const conflict = checkDateConflict(
                          selectedDateTime.toISOString(),
                          endDateTime.toISOString()
                        );
                        setDateConflict(conflict);
                      } else {
                        setSelectedSlot(null);
                        setDateConflict(null);
                      }
                    }}
                    disabled={submitting || !selectedInspector}
                    error={!!dateConflict}
                    helperText={
                      dateConflict
                        ? `Conflict: Inspector has another inspection at ${new Date(
                            dateConflict.inspectionDate
                          ).toLocaleString()}`
                        : "Select a date and time from the calendar or enter manually"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: "44px",
                        "& input": {
                          padding: "12px 14px",
                          fontSize: "0.875rem",
                        },
                      },
                    }}
                  />
                </MDBox>

                {/* Inspection Location */}
                <TextField
                  fullWidth
                  label="Inspection Location *"
                  name="inspectionLocation"
                  value={assignFormData.inspectionLocation}
                  onChange={(e) =>
                    setAssignFormData({ ...assignFormData, inspectionLocation: e.target.value })
                  }
                  placeholder="Enter the exact location for inspection"
                  multiline
                  rows={4}
                  disabled={submitting}
                  helperText="Location address will be auto-filled. You can edit if needed."
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              </MDBox>
            </Grid>

            {/* Right Column - Calendar */}
            <Grid item xs={12} lg={8}>
              <MDBox p={3} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <MDTypography variant="h6" fontWeight="bold" display="flex" alignItems="center">
                    <Icon sx={{ mr: 1, color: "info.main" }}>calendar_today</Icon>
                    Inspector Schedule
                    {selectedInspector &&
                      inspectors.find(
                        (insp) => String(insp._id || insp.id) === String(selectedInspector)
                      ) && (
                        <Chip
                          label={
                            inspectors.find(
                              (insp) => String(insp._id || insp.id) === String(selectedInspector)
                            )?.fullName
                          }
                          color="info"
                          size="small"
                          sx={{ ml: 2 }}
                        />
                      )}
                  </MDTypography>
                  {!selectedInspector && (
                    <Alert severity="info" sx={{ flex: 1, ml: 2 }}>
                      Please select an inspector to view their schedule
                    </Alert>
                  )}
                </MDBox>

                {selectedInspector ? (
                  <>
                    {loadingCalendar ? (
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ height: 600 }}
                      >
                        <CircularProgress />
                      </MDBox>
                    ) : (
                      <>
                        {/* Calendar Controls */}
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <MDBox display="flex" gap={1}>
                            <MDButton
                              variant="outlined"
                              color="info"
                              size="small"
                              onClick={() => navigateCalendar("PREV")}
                              startIcon={<Icon>chevron_left</Icon>}
                              sx={{ minHeight: "44px" }}
                            >
                              Previous
                            </MDButton>
                            <MDButton
                              variant="outlined"
                              color="info"
                              size="small"
                              onClick={() => setCalendarDate(new Date())}
                              sx={{ minHeight: "44px" }}
                            >
                              Today
                            </MDButton>
                            <MDButton
                              variant="outlined"
                              color="info"
                              size="small"
                              onClick={() => navigateCalendar("NEXT")}
                              endIcon={<Icon>chevron_right</Icon>}
                              sx={{ minHeight: "44px" }}
                            >
                              Next
                            </MDButton>
                          </MDBox>
                          <ButtonGroup variant="outlined" size="small">
                            <Button
                              variant={calendarView === "month" ? "contained" : "outlined"}
                              color="info"
                              onClick={() => handleViewChange("month")}
                              sx={{ minHeight: "44px" }}
                            >
                              Month
                            </Button>
                            <Button
                              variant={calendarView === "week" ? "contained" : "outlined"}
                              color="info"
                              onClick={() => handleViewChange("week")}
                              sx={{ minHeight: "44px" }}
                            >
                              Week
                            </Button>
                            <Button
                              variant={calendarView === "day" ? "contained" : "outlined"}
                              color="info"
                              onClick={() => handleViewChange("day")}
                              sx={{ minHeight: "44px" }}
                            >
                              Day
                            </Button>
                          </ButtonGroup>
                        </MDBox>

                        {/* Calendar */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: "background.default",
                            borderRadius: 2,
                            height: 600,
                            overflow: "visible",
                            position: "relative",
                            "& .rbc-calendar": {
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                            },
                            "& .rbc-time-view": {
                              border: "none",
                            },
                            "& .rbc-time-header": {
                              border: "1px solid #e0e0e0",
                            },
                            "& .rbc-time-content": {
                              borderTop: "2px solid #e0e0e0",
                            },
                            "& .rbc-month-view": {
                              "& .rbc-day-bg": {
                                minHeight: "120px",
                                border: "1px solid #e0e0e0",
                              },
                            },
                            "& .rbc-event": {
                              padding: "6px 10px",
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              borderRadius: "4px",
                              minHeight: "24px",
                            },
                            "& .rbc-selected": {
                              backgroundColor: "#4CAF50 !important",
                              border: "2px solid #2e7d32 !important",
                              boxShadow: "0 2px 8px rgba(76, 175, 80, 0.4) !important",
                              zIndex: 10,
                            },
                            "& .rbc-slot-reserved": {
                              backgroundColor: "rgba(244, 67, 54, 0.1) !important",
                              border: "1px dashed rgba(244, 67, 54, 0.3) !important",
                              cursor: "not-allowed !important",
                              pointerEvents: "none !important",
                              opacity: 0.6,
                            },
                          }}
                        >
                          <Calendar
                            localizer={localizer}
                            events={getCalendarEvents()}
                            startAccessor="start"
                            endAccessor="end"
                            style={{ height: "100%", minHeight: "550px" }}
                            view={calendarView}
                            date={calendarDate}
                            onView={handleViewChange}
                            onNavigate={setCalendarDate}
                            onSelectEvent={handleSelectEvent}
                            onSelectSlot={handleSelectSlot}
                            selectable
                            eventPropGetter={eventStyleGetter}
                            slotPropGetter={slotPropGetter}
                            popup
                            showMultiDayTimes
                            step={60}
                            timeslots={1}
                            min={new Date(2024, 0, 1, 8, 0)}
                            max={new Date(2024, 0, 1, 20, 0)}
                            defaultView={
                              calendarView === "month"
                                ? "month"
                                : calendarView === "week"
                                ? "week"
                                : "day"
                            }
                            formats={{
                              timeGutterFormat: "HH:mm",
                              eventTimeRangeFormat: ({ start, end }) =>
                                `${localizer.format(start, "HH:mm", "en")} - ${localizer.format(
                                  end,
                                  "HH:mm",
                                  "en"
                                )}`,
                              dayFormat: "ddd M/D",
                              dayHeaderFormat: "dddd M/D",
                            }}
                            components={{
                              event: ({ event }) => {
                                const isExisting = event.resource?.isExisting;
                                const isSelected = event.resource?.isSelected;
                                const eventStyle = eventStyleGetter(event);

                                return (
                                  <div
                                    data-existing={isExisting ? "true" : "false"}
                                    data-selected={isSelected ? "true" : "false"}
                                    style={{
                                      ...eventStyle.style,
                                      cursor: isExisting
                                        ? "pointer"
                                        : isSelected
                                        ? "pointer"
                                        : "default",
                                      width: "100%",
                                      height: "100%",
                                    }}
                                    onClick={(e) => {
                                      if (isExisting) {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSelectEvent(event);
                                      }
                                    }}
                                    title={
                                      isExisting
                                        ? `${String(
                                            event.title || "Existing Inspection"
                                          )} - Click to view details`
                                        : String(event.title || "Inspection")
                                    }
                                  >
                                    {event.title && String(event.title).trim() !== "" ? (
                                      <span
                                        style={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "block",
                                          width: "100%",
                                        }}
                                      >
                                        {String(event.title)}
                                      </span>
                                    ) : null}
                                  </div>
                                );
                              },
                            }}
                          />
                        </Paper>

                        {/* Legend */}
                        <MDBox display="flex" gap={2} mt={2} flexWrap="wrap">
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{ width: 20, height: 20, bgcolor: "#4CAF50", borderRadius: 1 }}
                            />
                            <MDTypography variant="caption">Selected Time</MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{ width: 20, height: 20, bgcolor: "#f44336", borderRadius: 1 }}
                            />
                            <MDTypography variant="caption">Existing Inspection</MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: "rgba(244, 67, 54, 0.1)",
                                border: "1px dashed rgba(244, 67, 54, 0.3)",
                                borderRadius: 1,
                              }}
                            />
                            <MDTypography variant="caption">Reserved Slot</MDTypography>
                          </MDBox>
                        </MDBox>
                      </>
                    )}
                  </>
                ) : (
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      height: 600,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                      border: "2px dashed",
                      borderColor: "grey.300",
                    }}
                  >
                    <MDBox textAlign="center">
                      <Icon sx={{ fontSize: "4rem", color: "grey.400", mb: 2 }}>
                        calendar_today
                      </Icon>
                      <MDTypography variant="h6" color="text.secondary" fontWeight="medium">
                        Select an Inspector
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary" mt={1}>
                        Choose an inspector from the dropdown to view their schedule
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                )}
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={handleCloseAssignDialog}
            disabled={submitting}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color={selectedProperty?.assignedInspector ? "warning" : "info"}
            onClick={handleAssignInspector}
            disabled={
              submitting ||
              !selectedInspector ||
              !assignFormData.inspectionDate ||
              !assignFormData.inspectionLocation.trim() ||
              !!dateConflict
            }
            startIcon={submitting ? <CircularProgress size={16} /> : <Icon>assignment</Icon>}
            sx={{
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
              },
            }}
          >
            {submitting
              ? selectedProperty?.assignedInspector
                ? "Reassigning..."
                : "Assigning..."
              : selectedProperty?.assignedInspector
              ? "Reassign Inspector"
              : "Assign Inspector"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Reject Property Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleCloseRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Property</DialogTitle>
        <DialogContent>
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text" mb={2}>
              You are about to reject the following property. This action cannot be undone.
            </MDTypography>
            {selectedProperty && (
              <MDBox p={2} borderRadius="lg" bgColor="grey.100" mb={2}>
                <MDTypography variant="subtitle2" fontWeight="medium">
                  {selectedProperty.title?.en || selectedProperty.title || "N/A"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  {selectedProperty.address?.en || selectedProperty.address || "N/A"},{" "}
                  {selectedProperty.city?.en || selectedProperty.city || "N/A"}
                </MDTypography>
              </MDBox>
            )}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Rejection Reason"
              placeholder="Please provide a reason for rejecting this property..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              disabled={submitting}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseRejectDialog} disabled={submitting}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleRejectProperty}
            variant="gradient"
            color="error"
            disabled={submitting || !rejectReason.trim()}
          >
            {submitting ? "Rejecting..." : "Reject Property"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Property Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!submitting) {
            setDeleteDialogOpen(false);
            setPropertyToDelete(null);
          }
        }}
      >
        <DialogTitle>Delete Property</DialogTitle>
        <DialogContent>
          <MDTypography>
            Permanently delete this property? This action cannot be undone and the property will be
            removed from the system.
          </MDTypography>
          {propertyToDelete && (
            <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {typeof propertyToDelete.title === "object"
                ? propertyToDelete.title?.en || propertyToDelete.title?.ar
                : propertyToDelete.title}{" "}
              {propertyToDelete.shortId ? `(${propertyToDelete.shortId})` : ""}
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setDeleteDialogOpen(false);
              setPropertyToDelete(null);
            }}
            disabled={submitting}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            onClick={handleDeleteProperty}
            disabled={submitting}
          >
            {submitting ? "Deleting..." : "Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* PDF Lightbox Viewer */}
      <Dialog
        open={pdfLightboxOpen}
        onClose={() => setPdfLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight="bold">
              {pdfLightboxTitle}
            </MDTypography>
            <MDBox display="flex" gap={1}>
              {!useGoogleViewer && (
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={<Icon>refresh</Icon>}
                  onClick={() => setUseGoogleViewer(true)}
                >
                  Use Google Viewer
                </MDButton>
              )}
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<Icon>download</Icon>}
                onClick={() => {
                  if (pdfLightboxUrl) {
                    const link = document.createElement("a");
                    link.href = pdfLightboxUrl;
                    link.download = `${pdfLightboxTitle}.pdf`;
                    link.target = "_blank";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                }}
              >
                Download
              </MDButton>
              <IconButton onClick={() => setPdfLightboxOpen(false)}>
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 0, height: "75vh", position: "relative", overflow: "hidden" }}
        >
          {pdfLightboxUrl && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: "75vh",
                position: "relative",
              }}
            >
              {useGoogleViewer ? (
                /* Google Docs Viewer as fallback */
                <Box
                  component="iframe"
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                    pdfLightboxUrl
                  )}&embedded=true`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    minHeight: "75vh",
                    border: "none",
                  }}
                  title={pdfLightboxTitle}
                />
              ) : (
                /* Try iframe first for Cloudinary PDFs */
                <Box
                  component="iframe"
                  src={`${pdfLightboxUrl}#view=FitH`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    minHeight: "75vh",
                    border: "none",
                  }}
                  title={pdfLightboxTitle}
                  onError={() => {
                    // If iframe fails, try Google Viewer
                    setUseGoogleViewer(true);
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default AdminProperties;
